import { useMemo } from 'react';
import {
  AlertOctagon,
  AlertTriangle,
  CheckCircle2,
  ChevronRight,
  Gauge,
  Mail,
  ShieldAlert,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import type { EnterpriseDetailRecord } from '@/components/enterprise/EnterpriseRecordDetailSheet';
import type {
  PartnerCampaignOverviewRow,
  PartnerSenderHealthRow,
  PartnerMemberQuotaRow,
} from '@/hooks/useEnterpriseEmailMonitoring';
import type { PartnerEmulationActivityRow } from '@/hooks/useEnterpriseEmulationActivity';

/**
 * M3 (#573): the unified triage queue.
 *
 * Composes the three M2 alert sources (erroring campaigns, disconnected senders,
 * quota-maxed members) + security-forced emulation closures into one list. Each
 * row shows a type chip, the affected workspace, a summary, and a "View" action.
 *
 * "View" no longer deep-links into another workspace (admins aren't members of
 * the workspaces they monitor, so `switchWorkspace` silently no-ops). Instead it
 * carries the FULL underlying source row and calls `onSelect(record)`, which the
 * page opens in an inline detail Sheet. Every alert type has a detail, so every
 * row gets a "View" — including emulation.
 *
 * Per the per-card discipline, this card owns its own skeleton + error + retry
 * driven off the SAME M2 queries (passed in), not a collapsed page-level state.
 */

/** One composed triage row, carrying its detail record + display fields. */
interface AlertQueueItem {
  key: string;
  /** The full source record to hand to the detail Sheet on "View". */
  record: EnterpriseDetailRecord;
  /** chip label, e.g. "Campaign" / "Sender" / "Quota" / "Emulation". */
  typeLabel: string;
  typeTone: 'destructive' | 'warning' | 'info';
  icon: typeof Mail;
  workspaceName: string;
  summary: string;
}

interface AlertQueuePanelProps {
  campaigns: PartnerCampaignOverviewRow[];
  senders: PartnerSenderHealthRow[];
  quotas: PartnerMemberQuotaRow[];
  emulation: PartnerEmulationActivityRow[];
  /** True while ANY of the four source queries is still loading. */
  isLoading: boolean;
  /** First error across the four source queries (per-card error state). */
  error: Error | null;
  /** Refetch all four source queries (per-card retry). */
  onRetry?: () => void;
  /**
   * Open an alert's underlying record in the inline detail Sheet. Replaces the
   * old cross-workspace deep-link navigation — no workspace switch.
   */
  onSelect: (record: EnterpriseDetailRecord) => void;
}

function buildItems(props: AlertQueuePanelProps): AlertQueueItem[] {
  const items: AlertQueueItem[] = [];

  for (const c of props.campaigns) {
    if (!c.needs_attention) continue;
    items.push({
      key: `campaign:${c.campaign_id}`,
      record: { type: 'campaign', data: c },
      typeLabel: 'Campaign',
      typeTone: 'destructive',
      icon: Mail,
      workspaceName: c.workspace_name,
      summary: `${c.campaign_name} — ${(c.failure_rate * 100).toFixed(1)}% failure`,
    });
  }

  for (const s of props.senders) {
    if (s.health_status === 'connected') continue;
    items.push({
      key: `sender:${s.workspace_id}:${s.email_address}`,
      record: { type: 'sender', data: s },
      typeLabel: 'Sender',
      typeTone: s.health_status === 'needs_reconnect' ? 'destructive' : 'warning',
      icon: AlertOctagon,
      workspaceName: s.workspace_name,
      summary:
        s.health_status === 'needs_reconnect'
          ? `${s.email_address} needs reconnect`
          : `${s.email_address} expired`,
    });
  }

  for (const q of props.quotas) {
    if (!q.near_wall) continue;
    items.push({
      key: `quota:${q.workspace_id}:${q.email_address}`,
      record: { type: 'quota', data: q },
      typeLabel: 'Quota',
      typeTone: 'warning',
      icon: Gauge,
      workspaceName: q.workspace_name,
      summary: `${q.email_address} at ${(q.pct_consumed * 100).toFixed(0)}% of daily limit`,
    });
  }

  for (const e of props.emulation) {
    if (!e.forced_by_security) continue;
    items.push({
      key: `emulation:${e.id}`,
      record: { type: 'emulation', data: e },
      typeLabel: 'Emulation',
      typeTone: 'info',
      icon: ShieldAlert,
      workspaceName: '—',
      summary: `Security-forced exit: ${e.admin_email ?? 'admin'} → ${
        e.target_email ?? 'member'
      }`,
    });
  }

  return items;
}

function toneBadge(tone: AlertQueueItem['typeTone'], label: string): JSX.Element {
  if (tone === 'destructive') {
    return (
      <Badge variant="destructive" className="gap-1">
        <AlertTriangle className="h-3 w-3" />
        {label}
      </Badge>
    );
  }
  if (tone === 'warning') {
    return (
      <Badge variant="default" className="gap-1 bg-yellow-500 hover:bg-yellow-500">
        {label}
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="gap-1 text-blue-600">
      {label}
    </Badge>
  );
}

export function AlertQueuePanel(props: AlertQueuePanelProps): JSX.Element {
  const items = useMemo(() => buildItems(props), [props]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <AlertTriangle className="h-5 w-5" />
          Alert queue
        </CardTitle>
      </CardHeader>
      <CardContent>
        {props.isLoading ? (
          <Skeleton className="h-40 w-full" />
        ) : props.error ? (
          <div className="flex h-40 flex-col items-center justify-center gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            <p className="text-sm text-muted-foreground">Couldn&rsquo;t load</p>
            {props.onRetry && (
              <Button variant="outline" size="sm" onClick={props.onRetry}>
                Retry
              </Button>
            )}
          </div>
        ) : items.length === 0 ? (
          <div className="py-8 text-center text-muted-foreground">
            <CheckCircle2 className="mx-auto mb-4 h-12 w-12 text-green-500" />
            <div>No active alerts</div>
            <div className="text-sm">Nothing across these workspaces needs attention.</div>
          </div>
        ) : (
          <ul className="divide-y">
            {items.map((item) => {
              const Icon = item.icon;
              return (
                <li
                  key={item.key}
                  className="flex items-center justify-between gap-4 py-3"
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        {toneBadge(item.typeTone, item.typeLabel)}
                        <span className="truncate text-sm font-medium">
                          {item.workspaceName}
                        </span>
                      </div>
                      <div className="truncate text-sm text-muted-foreground">
                        {item.summary}
                      </div>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    className="shrink-0"
                    onClick={() => props.onSelect(item.record)}
                  >
                    View
                    <ChevronRight className="ml-1.5 h-3.5 w-3.5" />
                  </Button>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
